import { FormEvent, useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Cliente {
  id: number;
  nome: string;
  documento: string | null;
  email: string | null;
  telefone: string | null;
  whatsapp: string | null;
  status: string;
}

function erroMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

const vazioParaNull = (v?: string | null): string | null => {
  const txt = (v || '').trim();
  return txt || null;
};

export default function ClientesOficina() {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [form, setForm] = useState<Partial<Cliente>>({ status: 'ativo' });
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [salvando, setSalvando] = useState(false);
  const editando = Boolean(form.id);

  async function carregar() {
    setErro('');
    setClientes(await api<Cliente[]>('/cadastros/clientes.php?incluir_inativos=1'));
  }

  useEffect(() => {
    carregar().catch((e) => setErro(erroMsg(e)));
  }, []);

  function limpar() {
    setForm({ status: 'ativo' });
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setMsg('');
    setSalvando(true);
    const body = {
      id: form.id,
      nome: (form.nome || '').trim(),
      documento: vazioParaNull(form.documento),
      email: vazioParaNull(form.email),
      telefone: vazioParaNull(form.telefone),
      whatsapp: vazioParaNull(form.whatsapp),
      status: form.status || 'ativo',
    };

    try {
      if (form.id) await api('/cadastros/clientes.php', { method: 'PUT', body });
      else await api('/cadastros/clientes.php', { method: 'POST', body });
      setMsg(editando ? 'Cliente atualizado.' : 'Cliente cadastrado.');
      limpar();
      await carregar();
    } catch (e2) {
      setErro(erroMsg(e2));
    } finally {
      setSalvando(false);
    }
  }

  // Cliente não se exclui: OS e orçamentos já criados apontam para ele.
  // Desativar preserva o histórico e tira o cliente das novas seleções.
  async function alternarStatus(c: Cliente) {
    const proximo = (c.status || 'ativo') === 'ativo' ? 'inativo' : 'ativo';
    if (!window.confirm(`Deseja ${proximo === 'ativo' ? 'reativar' : 'desativar'} este cliente?`)) return;
    setErro('');
    setMsg('');
    try {
      await api('/cadastros/clientes.php', { method: 'PUT', body: { id: c.id, status: proximo } });
      setMsg(proximo === 'ativo' ? 'Cliente reativado.' : 'Cliente desativado.');
      await carregar();
    } catch (e) {
      setErro(erroMsg(e));
    }
  }

  return (
    <div>
      <div className="titulo-pg">
        <h1>Clientes</h1>
        <button className="btn sec sm" onClick={() => carregar().catch((e) => setErro(erroMsg(e)))}>Atualizar</button>
      </div>

      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      <div className="grid cols2">
        <form className="card" onSubmit={salvar}>
          <h3>{editando ? 'Editar cliente' : 'Novo cliente'}</h3>
          <label>Nome *</label>
          <input className="input" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} required autoFocus />

          <div className="grid cols2">
            <div>
              <label>WhatsApp</label>
              <input className="input" value={form.whatsapp || ''} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
            </div>
            <div>
              <label>Telefone</label>
              <input className="input" value={form.telefone || ''} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
            </div>
          </div>

          <label>E-mail</label>
          <input className="input" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />

          <div className="grid cols2">
            <div>
              <label>Documento</label>
              <input className="input" value={form.documento || ''} onChange={(e) => setForm({ ...form, documento: e.target.value })} />
            </div>
            <div>
              <label>Status</label>
              <select value={form.status || 'ativo'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            {editando && <button type="button" className="btn sec" onClick={limpar}>Cancelar</button>}
            <button className="btn" disabled={salvando}>{salvando ? 'Salvando...' : (editando ? 'Salvar cliente' : 'Cadastrar cliente')}</button>
          </div>
        </form>

        <div className="tabela-wrap">
          <table>
            <thead><tr><th>Cliente</th><th>Contato</th><th>Documento</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {clientes.map((c) => (
                <tr key={c.id}>
                  <td><strong>{c.nome}</strong>{c.email ? <><br /><small>{c.email}</small></> : null}</td>
                  <td>{c.whatsapp || c.telefone || '-'}</td>
                  <td>{c.documento || '-'}</td>
                  <td><span className="badge">{c.status}</span></td>
                  <td className="acoes">
                    <button className="btn sec sm" onClick={() => setForm(c)}>Editar</button>
                    <button
                      className={`btn ${(c.status || 'ativo') === 'ativo' ? 'danger' : 'sec'} sm`}
                      onClick={() => alternarStatus(c)}
                    >
                      {(c.status || 'ativo') === 'ativo' ? 'Desativar' : 'Ativar'}
                    </button>
                  </td>
                </tr>
              ))}
              {!clientes.length && <tr><td colSpan={5} className="vazio">Nenhum cliente cadastrado.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
